import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Badge,
  Tooltip,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  WebAsset as WireframeIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Mic as MicIcon, 
  MicOff as MicOffIcon,
  AttachFile as AttachFileIcon,
  Language as LanguageIcon,
  Close as CloseIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import DocumentTypeTemplate from '../DocumentTypeTemplate';
import useNotification from '../../../hooks/useNotification';
import documentService from '../../../services/documentService';
import WireframeAccordion from '../../wireframes/WireframeAccordion';
import WireframePageConversation from '../../wireframes/WireframePageConversation';

// Styled components adapted from Conversation.js
const ChatMessageContainer = styled(Box)(({ theme, sender }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),
  width: '100%'
}));

const ChatBubble = styled(Paper)(({ theme, sender, isStreaming }) => ({
  padding: theme.spacing(2),
  maxWidth: sender === 'user' ? '100%' : '95%',
  width: sender === 'user' ? '100%' : 'auto',
  borderRadius: '12px',
  backgroundColor: sender === 'user' ? 'rgba(234, 240, 246, 1)' : 'white',
  color: theme.palette.text.primary,
  wordBreak: 'break-word',
  boxShadow: 'none',
  border: sender === 'user' ? '1px solid rgba(200, 210, 220, 0.8)' : 'none',
  ...(isStreaming && {
    border: '1px solid rgba(76, 175, 80, 0.3)',
    backgroundColor: 'rgba(244, 250, 244, 0.9)'
  }),
  [theme.breakpoints.down('sm')]: {
    maxWidth: sender === 'user' ? '100%' : '95%',
  }
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative'
}));

const MessageList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  paddingLeft: theme.spacing(6),
  paddingRight: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  scrollBehavior: 'smooth',
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(6),
  paddingRight: theme.spacing(6),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  position: 'sticky',
  bottom: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const FileUploadLabel = styled('label')(({ theme }) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: theme.spacing(1),
}));

const Wireframes = ({ projectId, existingContent = '', onSave }) => {
  const [showDirectChat, setShowDirectChat] = useState(false);
  const [wireframeMode, setWireframeMode] = useState('legacy'); // 'legacy' or 'pages'
  const [pageConversation, setPageConversation] = useState(null); // {pageId, pageTitle}
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: existingContent 
        ? "I see you have an existing wireframe. Tell me what changes you'd like to make, such as 'Change the color of the header', 'Add a search bar', or 'Remove the sidebar'."
        : "I'll help you generate wireframes for your application. Describe the layout, components, and functionality you need.", 
      timestamp: new Date().toISOString() 
    }
  ]);
  const [files, setFiles] = useState([]);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [isListening, setIsListening] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [wireframeHtml, setWireframeHtml] = useState(existingContent || '');
  const [localIsStreaming, setLocalIsStreaming] = useState(false);
  const [localStreamingContent, setLocalStreamingContent] = useState('');
  const [isEditing, setIsEditing] = useState(!!existingContent); // Track if we're editing existing content
  
  // Refs for reliable updates
  const messageListRef = useRef(null);
  const fileInputRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const streamingRef = useRef({
    isStreaming: false,
    content: '',
    lastUpdated: null
  });
  
  // Reference to hold auto-restart timeout
  const restartTimeoutRef = useRef(null);
  
  // Voice activity detection
  const speechTimeoutRef = useRef(null);
  const lastSpeechTimestampRef = useRef(Date.now());
  
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  // Available languages for speech recognition
  const availableLanguages = [
    { code: 'en-US', name: 'English (US)' },
    { code: 'en-GB', name: 'English (UK)' },
    { code: 'fr-FR', name: 'French' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'de-DE', name: 'German' },
    { code: 'es-ES', name: 'Spanish' },
    { code: 'it-IT', name: 'Italian' }
  ];
  
  // Initialize speech recognition
  useEffect(() => {
    // On mount, initialize streaming state
    setLocalStreamingContent('');
    setLocalIsStreaming(false);
    
    // Initialize Web Speech API on component mount once
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    // Only initialize if supported in the browser
    if (SpeechRecognition) {
      try {
        // Create the recognition instance once and store in a ref
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
        recognition.lang = selectedLanguage;
        
        // Store it in the ref for persistence
        speechRecognitionRef.current = recognition;
        
        console.log('Speech recognition initialized successfully');
      } catch (error) {
        console.error('Failed to initialize speech recognition:', error);
      }
    }
    
    return () => {
      // Cancel any ongoing streams when leaving the page
      
      // Clear local streaming state
      console.log("🧹 Cleaning up streaming state on unmount");
      setLocalStreamingContent('');
      setLocalIsStreaming(false);
      
      // Clean up speech recognition when component unmounts
      if (speechRecognitionRef.current) {
        try {
          console.log("Cleaning up speech recognition in unmount handler");
          
          // Mark as user stopped to prevent auto-restart attempts
          speechRecognitionRef.current._userStopped = true;
          
          // Try to stop in multiple ways for better browser compatibility
          try {
            // Try abort first (more reliable in some browsers)
            if (typeof speechRecognitionRef.current.abort === 'function') {
              speechRecognitionRef.current.abort();
            } else {
              speechRecognitionRef.current.stop();
            }
          } catch (stopError) {
            console.error('First cleanup stop attempt failed:', stopError);
            // Try the alternate method
            try {
              if (typeof speechRecognitionRef.current.stop === 'function') {
                speechRecognitionRef.current.stop();
              }
            } catch (error) {
              console.error('All cleanup stop attempts failed:', error);
            }
          }
        } catch (error) {
          console.error('Error stopping speech recognition during cleanup:', error);
        }
      }
      
      // Always update the state when unmounting to avoid UI inconsistencies
      setIsListening(false);
      setInterimTranscript('');
      
      // Clear all timeouts
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }
    };
  }, [selectedLanguage]);
  
  // Auto-scroll to bottom when messages change or during streaming
  useEffect(() => {
    // Scroll immediately
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
    
    // Scroll again after a short delay to ensure content is rendered
    setTimeout(() => {
      if (messageListRef.current) {
        messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
      }
    }, 50);
    
    // And scroll one more time for good measure
    setTimeout(() => {
      if (messageListRef.current) {
        messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
      }
    }, 200);
  }, [messages, localStreamingContent]);
  
  // Force UI update timer for streaming content
  useEffect(() => {
    // Set up an interval to periodically check if streaming content needs to be force-updated
    const intervalId = setInterval(() => {
      // If we're streaming according to the ref but not the state, force an update
      if (streamingRef.current.isStreaming) {
        const now = Date.now();
        
        // If we're streaming and haven't updated state in a while, force update
        if (streamingRef.current.lastUpdated && (now - streamingRef.current.lastUpdated) > 100) {
          console.log('🔄 Force updating streaming state from timer');
          setLocalIsStreaming(true);
          setLocalStreamingContent(streamingRef.current.content);
        }
      }
    }, 200); // Check every 200ms
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Toggle speech recognition on/off
  const toggleListening = () => {
    if (isListening) {
      // STOP LISTENING MODE - Using refs where possible to minimize state updates
      console.log('Speech recognition: stopping');
      
      // Get recognition from ref to avoid state dependencies
      const currentRecognition = speechRecognitionRef.current;
      
      // Clear any pending timeouts using refs
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
        speechTimeoutRef.current = null;
      }
      
      // Stop recognition if it exists - use ref instead of state
      if (currentRecognition) {
        try {
          // Set flag to prevent auto-restart
          currentRecognition._userStopped = true;
          
          // Try to stop the recognition
          if (typeof currentRecognition.abort === 'function') {
            currentRecognition.abort();
          } else if (typeof currentRecognition.stop === 'function') {
            currentRecognition.stop();
          }
        } catch (error) {
          console.error('Error stopping speech recognition:', error);
        }
      }
      
      // Now update UI state with a single re-render
      setIsListening(false);
      setInterimTranscript('');
    } else {
      // START LISTENING MODE
      console.log('Speech recognition: starting');
      
      // Use the persistent recognition instance from the ref
      const recognition = speechRecognitionRef.current;
      
      if (!recognition) {
        showNotification({
          message: "Speech recognition is not available in your browser.",
          type: "error"
        });
        return;
      }
      
      try {
        // Reset recognition state
        recognition._handlersSet = true;
        recognition._userStopped = false;
        recognition._failed = false;
        
        // Make sure the language is current
        recognition.lang = selectedLanguage;
        
        // Set up event handlers - avoid recreating them on each toggle
        // Only set them if not already set to prevent duplicates
        
        // Handle recognition results
        recognition.onresult = (event) => {
          try {
            let interimTranscript = '';
            let finalTranscript = '';
            
            // Reset the speech activity monitor since we're getting results
            lastSpeechTimestampRef.current = Date.now();
            
            // Process the recognition results
            for (let i = event.resultIndex; i < event.results.length; i++) {
              // Get the most confident transcript
              const transcript = event.results[i][0].transcript;
              
              if (event.results[i].isFinal) {
                finalTranscript += transcript;
              } else {
                interimTranscript += transcript;
              }
            }
            
            console.log(`Speech recognition result: final="${finalTranscript}", interim="${interimTranscript}"`);
            
            // Update the input field directly when final transcript is available
            if (finalTranscript) {
              // Check if the message contains a trigger word for sending
              const lowerFinalTranscript = finalTranscript.toLowerCase().trim();
              const sendTriggers = ['send', 'submit', 'send message', 'submit message', 'send it', 'go ahead'];
              
              if (sendTriggers.some(trigger => lowerFinalTranscript.includes(trigger))) {
                // Trigger sending the message, but first add any content before the trigger word
                const contentBeforeSend = finalTranscript.split(new RegExp(sendTriggers.join('|'), 'i'))[0].trim();
                if (contentBeforeSend) {
                  setMessage(prevMessage => prevMessage + contentBeforeSend + ' ');
                }
                
                // Small delay to ensure message state is updated
                setTimeout(() => {
                  // Trigger message send programmatically
                  handleMessageSend();
                }, 300);
              } else {
                // Regular update without sending
                setMessage(prevMessage => prevMessage + finalTranscript + ' ');
              }
            }
            
            // Show interim results without forcing full re-render
            if (interimTranscript !== '') {
              setInterimTranscript(interimTranscript);
            }
          } catch (resultError) {
            console.error('Error processing speech recognition results:', resultError);
          }
        };
        
        // Handle recognition errors with improved error handling
        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error, event);
          
          if (event.error === 'not-allowed') {
            showNotification({
              message: "Microphone access was denied. Please allow microphone access to use speech input.",
              type: "error"
            });
            recognition._failed = true;
            setIsListening(false);
          } else if (event.error === 'audio-capture') {
            showNotification({
              message: "No microphone was found. Ensure that a microphone is installed and configured correctly.",
              type: "error"
            });
            recognition._failed = true;
            setIsListening(false);
          } else if (event.error === 'network') {
            // Network errors might be temporary - don't mark as failed
            recognition._failed = false;
          } else if (event.error === 'aborted' || event.error === 'no-speech') {
            // These are normal, don't mark as failed and don't log as errors
            recognition._failed = false;
            console.log(`Normal speech recognition event: ${event.error}`);
          } else {
            // For other unknown errors, log but don't show to user unless it persists
            console.warn(`Speech recognition encountered error: ${event.error}`);
            if (event.error === 'service-not-allowed' || event.error === 'bad-grammar') {
              recognition._failed = true;
            }
          }
        };
        
        // Set up event handlers
        recognition.onstart = () => {
          console.log('Speech recognition started - persistent instance');
          
          // Reset activity monitoring using ref
          lastSpeechTimestampRef.current = Date.now();
          monitorSpeechActivity();
        };
        
        recognition.onend = () => {
          console.log('Speech recognition ended - maintaining state');
          
          // If not explicitly stopped by user, try to restart
          if (!recognition._userStopped && isListening) {
            // Auto-restart with delay
            restartTimeoutRef.current = setTimeout(() => {
              if (isListening) {
                try {
                  recognition.start();
                } catch (error) {
                  console.error('Failed to restart speech recognition:', error);
                  setIsListening(false);
                }
              }
            }, 300);
          } else {
            // Update UI if stopped
            setIsListening(false);
            setInterimTranscript('');
          }
        };
        
        // First update the UI state to show we're listening
        setIsListening(true);
        
        // Start recognition after a short delay to let the UI update first
        setTimeout(() => {
          try {
            // Start the recognition
            recognition.start();
            
            // Show notification after a slight delay
            setTimeout(() => {
              if (isListening) {
                showNotification({
                  message: `Listening in ${availableLanguages.find(lang => lang.code === selectedLanguage)?.name || 'selected language'}...`,
                  type: "info"
                });
              }
            }, 500);
          } catch (error) {
            console.error('Failed to start speech recognition:', error);
            setIsListening(false);
            showNotification({
              message: "Could not start voice input. Please try again.",
              type: "error"
            });
          }
        }, 50); // Slightly longer delay
      } catch (error) {
        console.error('Error in speech recognition setup:', error);
        setIsListening(false);
        showNotification({
          message: "Speech recognition setup failed. Please try again or use a different browser.",
          type: "error"
        });
      }
    }
  };
  
  // Monitor for periods of silence and restart if necessary
  const monitorSpeechActivity = () => {
    // Clear any existing timeout
    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
    }
    
    // Set a new timeout to check for silence
    speechTimeoutRef.current = setTimeout(() => {
      if (isListening) {
        const timeSinceLastSpeech = Date.now() - lastSpeechTimestampRef.current;
        
        // If no speech detected for 10 seconds, restart recognition
        if (timeSinceLastSpeech > 10000) {
          console.log('No speech activity detected for 10 seconds, restarting...');
          
          if (speechRecognitionRef.current) {
            try {
              speechRecognitionRef.current.stop();
              setTimeout(() => {
                if (isListening) {
                  speechRecognitionRef.current.start();
                  lastSpeechTimestampRef.current = Date.now();
                }
              }, 300);
            } catch (error) {
              console.error('Error restarting after silence:', error);
            }
          }
        }
        
        // Continue monitoring
        monitorSpeechActivity();
      }
    }, 5000); // Check every 5 seconds
  };
  
  // File upload handling
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Check file size limits
    const maxSizePerFile = 5 * 1024 * 1024; // 5MB per file
    const totalMaxSize = 10 * 1024 * 1024; // 10MB total
    
    let totalSize = 0;
    const validFiles = [];
    const oversizedFiles = [];
    
    selectedFiles.forEach(file => {
      totalSize += file.size;
      if (file.size > maxSizePerFile) {
        oversizedFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });
    
    // Handle oversized files
    if (oversizedFiles.length > 0) {
      showNotification({
        message: `${oversizedFiles.length} files exceed the 5MB limit: ${oversizedFiles.join(', ')}`,
        type: "warning"
      });
    }
    
    // Handle total size limit
    if (totalSize > totalMaxSize) {
      showNotification({
        message: `Total file size exceeds 10MB limit. Please select fewer or smaller files.`,
        type: "warning"
      });
      
      // Only keep files up to limit
      let currentSize = 0;
      const filteredFiles = [];
      for (const file of validFiles) {
        if (currentSize + file.size <= totalMaxSize) {
          filteredFiles.push(file);
          currentSize += file.size;
        }
      }
      setFiles(filteredFiles);
    } else {
      setFiles(validFiles);
    }
    
    if (validFiles.length > 0) {
      setFilesDialogOpen(true);
    }
  };
  
  // Process streaming updates from the wireframe generation API
  const processStreamResponse = async (response) => {
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    setLocalIsStreaming(true);
    streamingRef.current.isStreaming = true;
    
    try {
      let wireframeContent = '';
      let lastMessage = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Processing completed
          setLocalIsStreaming(false);
          streamingRef.current.isStreaming = false;
          
          // Final wireframe is complete
          if (wireframeContent && (wireframeContent.includes('<html') || wireframeContent.startsWith('<!DOCTYPE html>'))) {
            setWireframeHtml(wireframeContent);
            // Set isEditing to true since we now have wireframe content
            setIsEditing(true);
            
            // Add completion message
            setMessages(prev => [
              ...prev.filter(m => !m.isStreaming),
              { 
                role: 'assistant', 
                content: isEditing 
                  ? 'Wireframe updated successfully! You can see the changes in the preview panel.' 
                  : 'Wireframe generated successfully! You can see it in the preview panel.', 
                timestamp: new Date().toISOString() 
              }
            ]);
            
            // Save the content if callback provided
            if (onSave) {
              onSave(wireframeContent);
            }
          } else if (lastMessage) {
            // If we didn't get HTML but got text content, show it as a message
            setMessages(prev => [
              ...prev.filter(m => !m.isStreaming),
              { 
                role: 'assistant', 
                content: lastMessage || 'I wasn\'t able to generate a proper wireframe. Please try with more specific details about the UI layout you need.',
                timestamp: new Date().toISOString()
              }
            ]);
            
            showNotification({
              message: 'Unable to generate wireframe HTML. You can view the assistant\'s response in the chat.',
              type: 'warning'
            });
          } else {
            // No valid content was generated
            setMessages(prev => [
              ...prev.filter(m => !m.isStreaming),
              { 
                role: 'assistant', 
                content: 'I\'m sorry, I couldn\'t generate a wireframe from your description. Please provide more specific details about the layout, components, and functionality you need.',
                timestamp: new Date().toISOString()
              }
            ]);
          }
          break;
        }
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim() !== '');
        
        for (const line of lines) {
          try {
            const json = JSON.parse(line);
            
            if (json.status === 'streaming') {
              // Update streaming state
              lastMessage = json.message || lastMessage;
              streamingRef.current = {
                isStreaming: true,
                content: json.message || 'Generating wireframe...',
                lastUpdated: Date.now()
              };
              
              setLocalStreamingContent(json.message || 'Generating wireframe...');
              
              // Update progress message
              setMessages(prev => {
                const filtered = prev.filter(m => !m.isStreaming);
                return [
                  ...filtered,
                  { 
                    role: 'assistant', 
                    content: json.message || 'Generating wireframe...', 
                    timestamp: new Date().toISOString(),
                    isStreaming: true,
                    progress: json.progress || 0
                  }
                ];
              });
              
              // If HTML is provided in the stream, update the preview
              if (json.html && (json.html.includes('<html') || json.html.startsWith('<!DOCTYPE html>'))) {
                wireframeContent = json.html;
                setWireframeHtml(json.html);
              }
            } 
            else if (json.status === 'completed') {
              // Make sure the HTML is valid
              if (json.html && (json.html.includes('<html') || json.html.startsWith('<!DOCTYPE html>'))) {
                wireframeContent = json.html;
                setWireframeHtml(json.html);
              } else if (json.message) {
                // Store message if no valid HTML
                lastMessage = json.message;
              }
              
              // Update streaming state
              streamingRef.current = {
                isStreaming: false,
                content: '',
                lastUpdated: Date.now()
              };
              
              setLocalIsStreaming(false);
              setLocalStreamingContent('');
            }
            else if (json.status === 'error') {
              showNotification({
                message: `Error generating wireframe: ${json.message}`,
                type: 'error'
              });
              
              setMessages(prev => [
                ...prev.filter(m => !m.isStreaming),
                { 
                  role: 'assistant', 
                  content: `Error generating wireframe: ${json.message}. Please try again with a different prompt. Include specific details about the layout and components you need.`, 
                  timestamp: new Date().toISOString(),
                  isError: true
                }
              ]);
              
              // Update streaming state
              streamingRef.current = {
                isStreaming: false,
                content: '',
                lastUpdated: Date.now()
              };
              
              setLocalIsStreaming(false);
              setLocalStreamingContent('');
            }
          } catch (e) {
            console.error('Error parsing streaming response:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error processing stream:', error);
      setLocalIsStreaming(false);
      streamingRef.current.isStreaming = false;
      
      showNotification({
        message: `Error generating wireframe: ${error.message}`,
        type: 'error'
      });
      
      setMessages(prev => [
        ...prev.filter(m => !m.isStreaming),
        { 
          role: 'assistant', 
          content: 'Sorry, there was an error while generating the wireframe. Please try again with a more detailed description of what you need.',
          timestamp: new Date().toISOString(),
          isError: true
        }
      ]);
    }
  };
  
  // Send message and generate wireframe
  const handleMessageSend = async (e) => {
    // Allow e to be optional so we can call programmatically from voice recognition
    e?.preventDefault();
    if ((!message.trim() && files.length === 0) || localIsStreaming) return;
    
    try {
      // Create a more specific prompt for wireframe generation
      let messageContent = message.trim();
      
      // Handle differently based on whether we're editing or creating
      if (isEditing && wireframeHtml) {
        // For edits, include the existing HTML and be specific about editing
        const editPrefix = "I have an existing wireframe HTML that needs the following changes: ";
        // Append instruction to edit rather than create from scratch
        messageContent = `${editPrefix}${messageContent}\n\nPlease modify the existing HTML below. DO NOT regenerate the entire wireframe from scratch. Only make the specific changes requested:\n\n\`\`\`html\n${wireframeHtml}\n\`\`\``;
      } else {
        // For new wireframes, use the original enhancement logic
        if (!messageContent.toLowerCase().includes('wireframe') && 
            !messageContent.toLowerCase().includes('layout') &&
            !messageContent.toLowerCase().includes('design') &&
            !messageContent.toLowerCase().includes('ui')) {
          
          // Append instructions to force wireframe HTML generation
          messageContent = `${messageContent}\n\nPlease create a wireframe using HTML/CSS for this request. I need the actual HTML code that represents a visual wireframe layout, not a description.`;
        }
      }
      
      // Add user message to the chat using the original message (not the enhanced one)
      const userMessage = {
        role: 'user',
        content: message.trim(),
        timestamp: new Date().toISOString(),
        attachments: files.map(file => file.name)
      };
      
      setMessages(prev => [...prev, userMessage]);
      
      // Clear input immediately for better UX
      setMessage('');
      
      // Show typing indicator while we prepare
      setIsTyping(true);
      
      try {
        // Call the wireframe generation API with streaming, passing existing HTML if editing
        const response = await documentService.generateWireframeStream(
          projectId, 
          messageContent, 
          files,
          isEditing ? { isEdit: true, existingHtml: wireframeHtml } : undefined
        );
        
        // Hide typing indicator as we're about to start streaming
        setIsTyping(false);
        
        // Process the streaming response
        await processStreamResponse(response);
      } catch (err) {
        console.error("Failed in wireframe generation:", err);
        setIsTyping(false);
        setLocalIsStreaming(false);
        
        showNotification({
          message: "Failed to generate wireframe: " + (err.message || "Unknown error"),
          type: "error"
        });
        
        setMessages(prev => [
          ...prev,
          { 
            role: 'assistant', 
            content: `Sorry, I encountered an error while ${isEditing ? 'editing' : 'generating'} the wireframe: ${err.message || "Unknown error"}. Please try again with more specific details.`, 
            timestamp: new Date().toISOString(),
            isError: true
          }
        ]);
        
        return;
      }
    } catch (err) {
      setIsTyping(false);
      setLocalIsStreaming(false);
      console.error("Error sending message:", err);
      showNotification({
        message: `Error: ${err.message || "Failed to send message"}`,
        type: 'error'
      });
    } finally {
      setFiles([]);
    }
  };
  
  // Function to render a fallback wireframe when generation fails
  const renderFallbackWireframe = () => {
    const fallbackHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Wireframe Fallback</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f8f9fa;
            color: #333;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background-color: white;
            border-radius: 8px;
            padding: 30px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #e9ecef;
        }
        .section {
            margin: 20px 0;
            padding: 15px;
            border: 1px solid #dee2e6;
            border-radius: 5px;
        }
        .btn {
            background-color: #007bff;
            color: white;
            padding: 10px 20px;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            margin: 5px;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Basic Wireframe</h1>
            <p>This is a fallback wireframe template</p>
        </div>
        
        <div class="section">
            <h2>Main Content Area</h2>
            <p>This is where your main content would appear. You can modify this wireframe by chatting with the AI assistant.</p>
            <button class="btn">Primary Button</button>
            <button class="btn" style="background-color: #6c757d;">Secondary Button</button>
        </div>
        
        <div class="section">
            <h2>Navigation or Sidebar</h2>
            <ul>
                <li>Home</li>
                <li>About</li>
                <li>Services</li>
                <li>Contact</li>
            </ul>
        </div>
    </div>
</body>
</html>
    `;
    
    return (
      <Box>
        <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
          <Typography variant="body2" color="text.secondary">
            ℹ️ Using fallback wireframe template. Try describing your needs more specifically for a custom wireframe.
          </Typography>
        </Box>
        <Box sx={{ width: '100%', height: '600px', overflow: 'auto', border: '1px solid #ddd' }}>
          <iframe 
            srcDoc={fallbackHtml}
            title="Fallback Wireframe Preview"
            style={{ 
              width: '100%', 
              height: '100%', 
              border: 'none',
              background: 'white',
            }}
            sandbox="allow-scripts allow-same-origin"
          />
        </Box>
      </Box>
    );
  };
  
  // Function to render wireframe preview based on content type
  const renderWireframePreview = (content) => {
    if (!content) return renderFallbackWireframe();
    
    // Try to parse as HTML or JSON first
    try {
      if (content.startsWith('<!DOCTYPE html>') || content.includes('<html') || content.includes('<body')) {
        // HTML wireframe
        return (
          <Box sx={{ width: '100%', height: '600px', overflow: 'auto', border: '1px solid #ddd' }}>
            <iframe 
              srcDoc={content}
              title="Wireframe Preview"
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                background: 'white',
              }}
              sandbox="allow-scripts allow-same-origin"
            />
          </Box>
        );
      }
      
      // Try to parse as JSON
      const jsonData = JSON.parse(content);
      if (Array.isArray(jsonData) && jsonData.length > 0) {
        // JSON format with wireframes
        return (
          <Grid container spacing={2}>
            {jsonData.map((wireframe, index) => (
              <Grid item xs={12} md={6} lg={4} key={wireframe.id || index}>
                <Paper 
                  variant="outlined" 
                  sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  <Typography variant="subtitle1" gutterBottom>{wireframe.name || `Wireframe ${index+1}`}</Typography>
                  {wireframe.thumbnail || wireframe.image ? (
                    <Box 
                      component="img"
                      src={wireframe.thumbnail || wireframe.image}
                      alt={wireframe.name || `Wireframe ${index+1}`}
                      sx={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px',
                        flexGrow: 1,
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        bgcolor: '#f5f5f5', 
                        height: '200px', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexGrow: 1
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">No preview available</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        );
      }
      
      // Default to rendering as JSON string
      return (
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, overflowX: 'auto' }}>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(jsonData, null, 2)}</pre>
        </Box>
      );
    } catch (e) {
      // If not JSON or HTML, and the content seems like an error or failed generation
      if (content.toLowerCase().includes('error') || content.toLowerCase().includes('failed') || content.length < 50) {
        return renderFallbackWireframe();
      }
      
      // Otherwise render as markdown
      return (
        <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
          <ReactMarkdown>{content}</ReactMarkdown>
        </Box>
      );
    }
  };
  
  // Render the messages
  const renderMessages = () => {
    let messagesToRender = [...messages];
    
    // Add typing indicator if currently generating response
    if (isTyping) {
      messagesToRender.push({
        role: 'assistant',
        content: '...',
        isTyping: true,
        timestamp: new Date().toISOString()
      });
    }
    
    // Show streaming content
    if (localIsStreaming) {
      // Use the content from either the state or the ref (prefer ref as it's more reliable)
      const content = streamingRef.current.isStreaming ? 
        streamingRef.current.content : 
        localStreamingContent;
      
      // Create a streaming message with the content
      messagesToRender = [...messagesToRender.filter(m => !m.isStreaming), {
        role: 'assistant',
        content: content || '...', // Fallback if somehow empty
        isStreaming: true,
        timestamp: new Date().toISOString()
      }];
    }
    
    return messagesToRender.map((msg, index) => {
      const isAssistant = msg.role === 'assistant';
      
      return (
        <ChatMessageContainer key={index} sender={msg.role}>
          <Box mb={0.5} display="flex" alignItems="center">
            {/* Avatar for both user and assistant */}
            <Box 
              sx={{ 
                width: 28,
                height: 28,
                mr: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: msg.role === 'user' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                overflow: 'hidden'
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: msg.role === 'user' ? 'primary.main' : 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              >
                {msg.role === 'user' ? 'Y' : 'A'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {msg.role === 'user' ? (msg.createdByName || msg.created_by_name || 'You') : 'Wireframe Assistant'}
            </Typography>
          </Box>
          <Box sx={{ 
            pl: 5, /* Indent to align with avatar */
            pr: msg.role === 'user' ? 2 : 0, /* Add right padding for user messages */
            width: '100%' /* Full width for alignment */
          }}>
            <ChatBubble 
              sender={msg.role} 
              isStreaming={msg.isStreaming}>
              {msg.isTyping ? (
                <Box display="flex" alignItems="center" p={1}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Wireframe Assistant is thinking...
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Use ReactMarkdown for assistant messages to format them properly */}
                  {isAssistant ? (
                    <Box 
                      sx={{ 
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          fontWeight: 600,
                          marginTop: 2,
                          marginBottom: 1,
                        },
                        '& h1': { fontSize: '1.5rem' },
                        '& h2': { fontSize: '1.3rem' },
                        '& h3': { fontSize: '1.1rem' },
                        '& p': { marginBottom: 1.5 },
                        '& ul, & ol': { paddingLeft: 3, marginBottom: 1.5 },
                        '& li': { marginBottom: 0.5 },
                        '& code': {
                          backgroundColor: '#f5f5f5',
                          padding: '2px 4px',
                          borderRadius: 4,
                          fontSize: '0.9em',
                        },
                        '& pre': {
                          backgroundColor: '#f5f5f5',
                          padding: 1.5,
                          borderRadius: 4,
                          overflowX: 'auto',
                          marginBottom: 1.5,
                        }
                      }}
                    >
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body1">
                      {msg.content}
                    </Typography>
                  )}
                  
                  {/* Display attachments if any */}
                  {msg.attachments && msg.attachments.length > 0 && (
                    <Box mt={2} mb={1} sx={{ borderTop: '1px solid rgba(0,0,0,0.1)', pt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                        {msg.attachments.length === 1 ? 'Attached file:' : `Attached files (${msg.attachments.length}):`}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1, 
                        alignItems: 'center' 
                      }}>
                        {msg.attachments.map((attachment, i) => (
                          <Box 
                            key={i} 
                            sx={{ 
                              display: 'flex', 
                              flexDirection: 'column',
                              alignItems: 'center',
                              border: '1px solid rgba(0,0,0,0.1)',
                              borderRadius: 1,
                              padding: 1,
                              bgcolor: 'rgba(0,0,0,0.03)'
                            }}
                          >
                            <DescriptionIcon sx={{ fontSize: 20, color: 'primary.main', mb: 0.5 }} />
                            <Typography variant="caption">{attachment}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  {/* Progress indicator for streaming messages */}
                  {(msg.isStreaming) && (
                    <>
                      {/* Simple streaming indicator */}
                      <Typography 
                        variant="body2" 
                        color="primary.main"
                        sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
                      >
                        <span style={{ color: '#2196f3', marginRight: '6px' }}>●</span>
                        Wireframe Assistant is generating...
                      </Typography>
                      
                      {/* Progress bar if available */}
                      {msg.progress > 0 && (
                        <Box sx={{ width: '100%', mt: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={msg.progress} 
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                            {Math.round(msg.progress)}% complete
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </ChatBubble>
          </Box>
        </ChatMessageContainer>
      );
    });
  };
  
  // Wireframe-specific prompt configuration
  const wireframePromptConfig = {
    system: "You are a UI/UX specialist skilled in creating wireframes and mockups for web and mobile applications.",
    examples: [
      "Create a wireframe for a product listing page with filters and sorting",
      "Design a user profile dashboard wireframe with activity stats"
    ],
    instruction: "Create detailed wireframes based on the user's requirements. Focus on layout, information hierarchy, and user flows.",
    buttonText: "Generate with AI",                // Custom button text to replace "Start Chat"
    tileTitle: "Generate Wireframes",              // Custom tile title to replace "Ask AI"
    chatTitle: "Project Info",                     // Custom title for the chat dialog
    placeholder: "Describe the wireframe you need..."  // Custom placeholder for chat input
  };
  
  // Page conversation mode
  if (pageConversation) {
    return (
      <WireframePageConversation
        projectId={projectId}
        pageId={pageConversation.pageId}
        pageTitle={pageConversation.pageTitle}
        onClose={() => setPageConversation(null)}
        onPageUpdate={(pageId, updatedPage) => {
          // Handle page update if needed
          if (onSave) {
            onSave(updatedPage.html_content);
          }
        }}
      />
    );
  }

  // Page-based accordion mode
  if (wireframeMode === 'pages') {
    return (
      <Box sx={{ p: 3 }}>
        {/* Mode switcher */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h5">Page-Based Wireframes</Typography>
          <Button
            variant="outlined"
            onClick={() => setWireframeMode('legacy')}
            size="small"
          >
            Switch to Legacy Mode
          </Button>
        </Box>

        <WireframeAccordion
          projectId={projectId}
          onPageUpdate={(pageId, updatedPage) => {
            if (onSave) {
              onSave(updatedPage.html_content);
            }
          }}
          showConversation={(pageId, pageTitle) => {
            setPageConversation({ pageId, pageTitle });
          }}
        />
      </Box>
    );
  }

  // Direct chat mode shows the conversation component
  if (showDirectChat) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 0, boxShadow: 'none' }}>
        {/* Mode switcher in direct chat */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center', 
          p: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid rgba(0,0,0,0.12)'
        }}>
          <Typography variant="body2" color="text.secondary">
            Wireframe Mode:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={wireframeMode === 'legacy' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setWireframeMode('legacy')}
            >
              Legacy Mode
            </Button>
            <Button
              variant={wireframeMode === 'pages' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => {
                setWireframeMode('pages');
                setShowDirectChat(false); // Exit direct chat when switching to pages
              }}
            >
              Page-Based Accordion
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {wireframeMode === 'pages' 
              ? '🎯 98% token savings with surgical precision' 
              : '⚡ Traditional single wireframe approach'
            }
          </Typography>
        </Box>
        <Grid container spacing={0} sx={{ height: 'calc(100% - 70px)' }}>
          {/* Left side - Conversation Component */}
          <Grid item xs={12} md={6} sx={{ 
            height: '100%', 
            borderRight: '1px solid rgba(0, 0, 0, 0.12)', 
          }}>
            {/* Chat interface - styled like the Conversation page but without the layout */}
            <ChatContainer>
              {/* Header */}
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)', 
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton 
                    size="small" 
                    sx={{ mr: 1 }}
                    onClick={() => setShowDirectChat(false)}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h6">Project Info</Typography>
                </Box>
              </Box>
              
              {/* Message area */}
              <MessageList ref={messageListRef}>
                {renderMessages()}
              </MessageList>
              
              {/* Input area */}
              <InputContainer>
                <form onSubmit={handleMessageSend} style={{ maxWidth: '1200px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Action buttons row */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1,
                    borderRadius: '20px',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    p: 0.75,
                    px: 1.5
                  }}>
                    {/* Voice input button */}
                    <Tooltip title={isListening ? "Stop listening" : "Voice input"}>
                      <IconButton
                        size="small"
                        onClick={toggleListening}
                        color={isListening ? "error" : "default"}
                        sx={{ 
                          animation: isListening ? 'pulse 1.5s infinite' : 'none',
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(1)', opacity: 1 },
                            '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                            '100%': { transform: 'scale(1)', opacity: 1 }
                          },
                          mr: 1
                        }}
                      >
                        <MicIcon fontSize="small" />
                        {isListening && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '-4px',
                              right: '-4px',
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: 'green',
                              boxShadow: '0 0 4px #00ff00'
                            }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                    
                    {/* Language selection button */}
                    <Tooltip title="Select language">
                      <IconButton
                        size="small"
                        onClick={() => setLanguageDialogOpen(true)}
                        sx={{ mr: 1 }}
                      >
                        <LanguageIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Divider orientation="vertical" flexItem sx={{ height: 20, mx: 0.5 }} />
                    
                    {/* File upload button */}
                    <FileUploadLabel htmlFor="file-upload">
                      <Tooltip title="Upload files or images to include with your request">
                        <IconButton 
                          component="span" 
                          size="small" 
                          sx={{ 
                            mr: 1, 
                            color: files.length > 0 ? 'primary.main' : 'inherit',
                            '&:hover': {
                              bgcolor: 'rgba(25, 118, 210, 0.08)',
                              color: 'primary.main'
                            }
                          }}
                        >
                          <Badge
                            badgeContent={files.length}
                            color="primary"
                            invisible={files.length === 0}
                          >
                            <AttachFileIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </FileUploadLabel>
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt,.md,.csv,.json,.xml"
                      onChange={handleFileSelect}
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                    />
                  </Box>
                  
                  {/* Input field and send button row */}
                  <Grid container spacing={1} alignItems="flex-end">
                    <Grid item xs>
                      <TextField
                        fullWidth
                        placeholder={isListening ? `Listening in ${availableLanguages.find(lang => lang.code === selectedLanguage)?.name || 'selected language'}...` : "Describe the wireframe you need..."}
                        variant="outlined"
                        size="medium"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => {
                          // Only submit on Enter without Shift key
                          if (e.key === 'Enter' && !e.shiftKey) {
                            handleMessageSend(e);
                          }
                        }}
                        multiline
                        maxRows={4}
                        helperText={
                          isListening ? 
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            animation: 'fadeInOut 1.5s infinite',
                            '@keyframes fadeInOut': {
                              '0%': { opacity: 0.6 },
                              '50%': { opacity: 1 },
                              '100%': { opacity: 0.6 }
                            }
                          }}>
                            <MicIcon sx={{ fontSize: 14, mr: 0.5, color: 'green' }} /> 
                            {interimTranscript ? `Transcribing: ${interimTranscript}` : 'Listening...'}
                          </Box> 
                          : ''
                        }
                      />
                    </Grid>
                    <Grid item sx={{ mb: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={localIsStreaming}
                        sx={{ 
                          height: '40px',
                          minWidth: '40px',
                          width: '40px',
                          borderRadius: '50%',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {localIsStreaming ? 
                          <CircularProgress size={20} /> : 
                          <SendIcon />
                        }
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </InputContainer>
            </ChatContainer>
          </Grid>
          
          {/* Right side - Preview */}
          <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)', 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'white'
            }}>
              <Typography variant="h6">Wireframe Preview</Typography>
              {wireframeHtml && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<LanguageIcon />}
                    size="small"
                    onClick={() => {
                      // Create a blob URL and open in new tab
                      const blob = new Blob([wireframeHtml], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                      
                      // Clean up the URL after a short delay
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    }}
                  >
                    View HTML
                  </Button>
                  <Button 
                    variant="outlined" 
                    startIcon={<SaveIcon />}
                    size="small"
                    onClick={() => {
                      if (wireframeHtml && onSave) {
                        onSave(wireframeHtml);
                        showNotification({
                          message: 'Wireframe saved successfully',
                          type: 'success'
                        });
                      }
                    }}
                  >
                    Save Wireframe
                  </Button>
                </Box>
              )}
            </Box>
            
            {/* Preview content */}
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'auto', 
              p: 2, 
              bgcolor: '#f8f9fa'
            }}>
              {wireframeHtml ? (
                renderWireframePreview(wireframeHtml)
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  color: '#A0AEC0'
                }}>
                  <Box sx={{ 
                    width: 100, 
                    height: 100, 
                    border: '2px dashed #D1D5DB', 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <WireframeIcon sx={{ fontSize: 50, color: '#CBD5E0' }} />
                  </Box>
                  <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 1 }}>
                    Wireframe preview will appear here
                  </Typography>
                  <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
                    Ask the AI to generate a wireframe to see results
                  </Typography>
                  <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<WireframeIcon />}
                    onClick={() => {
                      // Load the fallback HTML directly
                      const fallbackHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Wireframe Template</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f8f9fa;
            color: #333;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background-color: white;
            border-radius: 8px;
            padding: 30px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #e9ecef;
        }
        .section {
            margin: 20px 0;
            padding: 15px;
            border: 1px solid #dee2e6;
            border-radius: 5px;
        }
        .btn {
            background-color: #007bff;
            color: white;
            padding: 10px 20px;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            margin: 5px;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Basic Wireframe Template</h1>
            <p>Start here and modify this template using the AI assistant</p>
        </div>
        
        <div class="section">
            <h2>Main Content Area</h2>
            <p>This is where your main content would appear. You can modify this wireframe by chatting with the AI assistant.</p>
            <button class="btn">Primary Button</button>
            <button class="btn" style="background-color: #6c757d;">Secondary Button</button>
        </div>
        
        <div class="section">
            <h2>Navigation or Sidebar</h2>
            <ul>
                <li>Home</li>
                <li>About</li>
                <li>Services</li>
                <li>Contact</li>
            </ul>
        </div>
    </div>
</body>
</html>
                      `;
                      
                      setWireframeHtml(fallbackHtml);
                      setIsEditing(true);
                      showNotification({
                        message: 'Loaded fallback wireframe template. You can now edit it using the chat.',
                        type: 'info'
                      });
                    }}
                  >
                    Load Template
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
        
        {/* Language Selection Dialog */}
        <Dialog 
          open={languageDialogOpen} 
          onClose={() => setLanguageDialogOpen(false)}
        >
          <DialogTitle>Select Voice Input Language</DialogTitle>
          <DialogContent>
            <List sx={{ minWidth: 250 }}>
              {availableLanguages.map((language) => (
                <ListItem
                  key={language.code}
                  button
                  onClick={() => {
                    setSelectedLanguage(language.code);
                    
                    // If speech recognition is active, restart it with the new language
                    if (isListening && speechRecognitionRef.current) {
                      speechRecognitionRef.current.stop();
                      
                      // Wait a moment for the recognition to stop before restarting
                      setTimeout(() => {
                        speechRecognitionRef.current.lang = language.code;
                        speechRecognitionRef.current.start();
                        
                        showNotification({
                          message: `Now listening in ${language.name}...`,
                          type: "info"
                        });
                      }, 300);
                    }
                    
                    setLanguageDialogOpen(false);
                  }}
                  selected={selectedLanguage === language.code}
                  sx={{ 
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(33, 150, 243, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.12)',
                      }
                    }
                  }}
                >
                  <Typography variant="body1">{language.name}</Typography>
                  {selectedLanguage === language.code && <MicIcon color="primary" sx={{ ml: 1 }} />}
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLanguageDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
        
        {/* Files Dialog */}
        <Dialog 
          open={filesDialogOpen} 
          onClose={() => setFilesDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Upload Files</DialogTitle>
          <DialogContent>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" color="primary" gutterBottom fontWeight={500}>
                Selected files:
              </Typography>

              <List sx={{ 
                maxHeight: '200px', 
                overflow: 'auto', 
                border: '1px solid rgba(0,0,0,0.12)', 
                borderRadius: 1, 
                mb: 2 
              }}>
                {files.map((file, index) => (
                  <ListItem key={index} 
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" size="small"
                        onClick={() => {
                          const newFiles = [...files];
                          newFiles.splice(index, 1);
                          setFiles(newFiles);
                          if (newFiles.length === 0) {
                            setFilesDialogOpen(false);
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {file.type.includes('image') ? 
                        <Box component="img" 
                          src={URL.createObjectURL(file)} 
                          alt={file.name}
                          sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, mr: 2 }}
                        /> :
                        <DescriptionIcon color="primary" sx={{ mr: 2 }} />
                      }
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`${(file.size / 1024 / 1024).toFixed(2)} MB • ${file.type || 'Unknown type'}`}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1, 
                border: '1px dashed rgba(0,0,0,0.2)', 
                borderRadius: 1, 
                p: 2,
                bgcolor: 'rgba(0,0,0,0.01)'
              }}>
                <Typography variant="subtitle2" color="primary">
                  Add more files:
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  size="small"
                  component="label"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Select File
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.txt,.md,.csv,.json,.xml"
                    onChange={handleFileSelect}
                    hidden
                    multiple
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: Images, PDF, DOC, DOCX, TXT, MD, CSV, JSON, XML (Max 5MB per file, 10MB total)
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilesDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={() => {
                setFilesDialogOpen(false);
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Override the DocumentTypeTemplate's Ask AI button to directly show our chat
  const customHandlers = {
    onChatButtonClick: () => setShowDirectChat(true)
  };

  // Custom header for mode switching
  const customHeader = (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 2, 
      mb: 2,
      p: 2,
      bgcolor: 'background.paper',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider'
    }}>
      <Typography variant="body2" color="text.secondary">
        Wireframe Mode:
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant={wireframeMode === 'legacy' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setWireframeMode('legacy')}
        >
          Legacy Mode
        </Button>
        <Button
          variant={wireframeMode === 'pages' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setWireframeMode('pages')}
        >
          Page-Based Accordion
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
        {wireframeMode === 'pages' 
          ? '🎯 98% token savings with surgical precision' 
          : '⚡ Traditional single wireframe approach'
        }
      </Typography>
    </Box>
  );

  return (
    <Box>
      {customHeader}
      <DocumentTypeTemplate
        projectId={projectId}
        documentType="wireframes"
        title="Wireframes"
        icon={WireframeIcon}
        existingContent={existingContent || wireframeHtml}
        onSave={onSave}
        renderPreview={renderWireframePreview}
        promptConfig={wireframePromptConfig}
        allowedImportTypes="application/json,text/plain,text/html,text/markdown,image/*"
        useSplitView={true}
        customHandlers={customHandlers}
      >
      {/* Additional wireframe-specific content */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Wireframing Guidelines</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Best Practices</Typography>
            <ul>
              <li>Start with low-fidelity wireframes to focus on layout and structure</li>
              <li>Keep wireframes simple and minimalistic</li>
              <li>Focus on user flows and navigation between screens</li>
              <li>Use consistent patterns for common elements</li>
              <li>Annotate wireframes to explain functionality</li>
              <li>Link wireframes to specific features and user stories</li>
              <li>Get feedback early and iterate based on input</li>
            </ul>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>Common Components</Typography>
            <ul>
              <li>Navigation (menus, tabs, breadcrumbs)</li>
              <li>Input controls (buttons, text fields, checkboxes)</li>
              <li>Information components (tooltips, icons, progress bars)</li>
              <li>Containers (accordion, cards, tables)</li>
              <li>Lists and data grids</li>
              <li>Modal dialogs and popovers</li>
              <li>Charts and data visualization</li>
            </ul>
          </Grid>
        </Grid>
      </Paper>
      </DocumentTypeTemplate>
    </Box>
  );
};

export default Wireframes;